import React, { ReactChild, ReactNode } from 'react';
import './OptionalLabel.less';
import Label from '../Label/Label';
import classNames from 'classnames';
import { useId } from '../../../helper/useIdHook';
import { useFieldHidden } from '../../../fieldVisibility/useFieldVisibility';


interface OptionalLabelType {
    /**
     * Key der Angabe im Feld-Katalog. Ist sie portalweit ausgeblendet, rendert
     * diese Komponente nichts.
     *
     * Der Ort ist bewusst gewählt: OptionalLabel entscheidet ohnehin schon „kein
     * Wert, keine Zeile". „Ausgeblendet, keine Zeile" ist derselbe Satz mit
     * anderem Anlass — es kommt keine neue Idee in die Komponente, nur ein
     * zweiter Anlass für dieselbe Entscheidung.
     *
     * Fehlt das Prop, gilt „nicht im Katalog geführt, nie versteckt". Die über 40
     * Aufrufe für Ablesedaten bleiben dadurch unberührt.
     *
     * Beantwortet NUR die portalweite Frage. Ein tabellenlokales Ausblenden
     * gehört in die Spalten-Pipeline: diese Komponente weiß nicht, in welcher
     * Tabelle sie steckt.
     */
    field?: string;
    label?: string | undefined | ReactNode;
    children: string | undefined | ReactNode;
    description?: string | ReactNode;
    className?: string;
    labelClassName?: string;
}

const OptionalLabel = ({ field, label, children, description, className, labelClassName }: OptionalLabelType) => {
    // Der Hook steht VOR dem frühen return — er darf nicht bedingt laufen.
    const id = useId();

    if (!children) return null;
    const content = (
        <>
            <Label id={label ? id : undefined} className={classNames(labelClassName)}>
                {label || ''}
            </Label>
            <div aria-labelledby={label ? id : undefined}>
                <div className={className}>{children}</div>
                {description && <div className={'optional-label-description text-sm'}>{description}</div>}
            </div>
        </>
    );

    // Nur mit `field` hängt die Zeile am Store. Ohne das Prop bleibt OptionalLabel
    // eine reine Darstellungskomponente: kein Store-Abo je Label und kein
    // Provider-Zwang in Tests.
    return field === undefined ? content : <HiddenFieldGate field={field}>{content}</HiddenFieldGate>;
};

const HiddenFieldGate = ({ field, children }: { field: string; children: ReactNode }) => {
    const hidden = useFieldHidden(field);
    return hidden ? null : <>{children}</>;
};

export default OptionalLabel;
